import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { errMsg } from "@/components/attendance/ui";
import SubscriptionBanner from "@/components/attendance/SubscriptionBanner";
import Console from "@/components/projection/Console";
import TeamPanel from "@/components/attendance/TeamPanel";

export default function ProjectionConsole() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [meta, setMeta] = useState(null);
  const [plan, setPlan] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/projection/console"); return; }
    (async () => {
      try {
        if (params.get("billing") === "success" && params.get("session_id")) {
          await api.post(`/attendance/billing/sync?session_id=${encodeURIComponent(params.get("session_id"))}`).catch(() => {});
          toast.success("Your Hymn Projection subscription has started");
          setParams({});
        } else if (params.get("billing") === "cancelled") { toast("Checkout cancelled — you can subscribe anytime."); setParams({}); }
        const [m, x, p] = await Promise.all([api.get("/projection/me"), api.get("/projection/meta"), api.get("/attendance/plans")]);
        setMe(m.data); setMeta(x.data); setPlan(p.data);
      } catch (e) {
        if (e?.response?.status === 404) nav("/projection/register");
        else toast.error(errMsg(e, "Could not load Hymn Projection"));
      }
    })();
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!me || !meta) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  const total = meta.hymn_counts.en + meta.hymn_counts.yo;
  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="projection-console-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="label-eyebrow mb-2">HOH Worship Projection</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-white" data-testid="projection-org-name">{me.org?.name || "Presenter console"}</h1>
        </div>
        <div className="text-xs text-neutral-500" data-testid="projection-library-count">{meta.hymn_counts.en} English · {meta.hymn_counts.yo} Yoruba hymns</div>
      </div>
      {me.org && me.authorised && <div className="mb-6"><SubscriptionBanner org={me.org} plan={plan} product="projection" returnPath="/projection/console" canBill={me.my_roles?.includes("owner")} /></div>}
      {me.org && !me.authorised && (
        <p className="luxury-card rounded-xl p-6 text-sm text-neutral-300 mb-6" data-testid="projection-not-authorised">You are not a Hymn Projection administrator for {me.org.name}. Ask the church account owner ({me.org.owner_email}) for access.</p>
      )}
      {me.my_roles?.includes("owner") && me.org && (
        <div className="mb-6">
          <button onClick={() => setTeamOpen((o) => !o)} className="text-xs inline-flex items-center gap-2 text-neutral-400 hover:text-[#D4AF37]" data-testid="projection-team-toggle"><Users size={14} /> {teamOpen ? "Hide church team" : "Manage church team"}</button>
          {teamOpen && <div className="mt-4"><TeamPanel /></div>}
        </div>
      )}
      {me.has_access && (total ? <Console meta={meta} me={me} /> : (
        <p className="luxury-card rounded-xl p-6 text-sm text-neutral-400" data-testid="projection-library-empty">The hymn library is being prepared. Hymns will appear here as soon as they are published.</p>
      ))}
    </div>
  );
}
